import { For } from "solid-js";
import { KpiCard, HealthBar, Sparkline, Tag, LayerBadges } from "./ui";
import { MOCK_PLUGIN_TELEMETRY, MOCK_KPI, MOCK_INVOC_STREAM } from "./mock-tenants";

// ─── Helpers ───────────────────────────────────────────────────────────────

function statusVariant(status: string): "ok" | "info" | "warn" | undefined {
  if (status === "enabled") return "ok";
  if (status === "beta") return "info";
  if (status === "staging") return "warn";
  return undefined; // "disabled" → pas de variant (text-4 via tag par défaut)
}

/** Colorie ✓ en vert et ✗ en rouge dans une ligne d'invocation. */
function renderInvocLine(line: string) {
  const parts = line.split(/(✓|✗)/);
  return (
    <span>
      <For each={parts}>
        {(part) => {
          if (part === "✓") return <span class="invoc-ok">✓</span>;
          if (part === "✗") return <span class="invoc-err">✗</span>;
          return <span>{part}</span>;
        }}
      </For>
    </span>
  );
}

// ─── PluginsConsolePage ────────────────────────────────────────────────────

export function PluginsConsolePage() {
  const errorRate =
    MOCK_KPI.calls7d > 0 ? ((MOCK_KPI.errors7d / MOCK_KPI.calls7d) * 100).toFixed(1) + "% du total" : "0% du total";

  return (
    <div class="plugins-console">
      {/* En-tête */}
      <div class="plugins-console-header">
        <span class="plugins-console-title">
          Plugins
          <span class="plugins-console-sub">dashboard V6</span>
        </span>
      </div>

      {/* ── Section 1 : KPI Grid ── */}
      <div class="kpi-grid">
        <KpiCard label="Appels 7j" value={MOCK_KPI.calls7d.toLocaleString()} level="ok" />

        <KpiCard
          label="Erreurs 7j"
          value={MOCK_KPI.errors7d.toString()}
          level={MOCK_KPI.errors7d > 0 ? "warn" : "ok"}
          sub={errorRate}
        />

        <KpiCard
          label="Plugins en alerte"
          value={MOCK_KPI.pluginsAlert.toString()}
          level={MOCK_KPI.pluginsAlert > 0 ? "danger" : "ok"}
          sub="santé < 90%"
        />

        <KpiCard
          label="Latence p95"
          value={MOCK_KPI.latencyP95ms + "ms"}
          level={MOCK_KPI.latencyP95ms > 500 ? "warn" : "ok"}
          sub="sur 7 jours"
        />
      </div>

      {/* ── Section 2 : Table santé ── */}
      <div class="rmm-table-wrap" style={{ flex: "1 1 0", "min-height": "0" }}>
        <table class="rmm-table">
          <thead>
            <tr>
              <th>Plugin</th>
              <th>Status</th>
              <th>Santé</th>
              <th>Appels 7j</th>
              <th>Erreurs 7j</th>
              <th>Latence p95</th>
              <th>Dernière erreur</th>
              <th>Couches</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <For each={MOCK_PLUGIN_TELEMETRY}>
              {(plugin) => (
                <tr>
                  {/* Plugin : nom + id */}
                  <td>
                    <span style={{ color: "var(--text-1)", "font-weight": "500" }}>{plugin.name}</span>
                    <br />
                    <span
                      style={{
                        "font-family": "var(--font-mono)",
                        "font-size": "10px",
                        color: "var(--text-4)",
                      }}
                    >
                      {plugin.id}
                    </span>
                  </td>

                  {/* Status badge */}
                  <td>
                    <Tag variant={statusVariant(plugin.status)}>{plugin.status}</Tag>
                  </td>

                  {/* Santé */}
                  <td>
                    {plugin.status === "disabled" ? (
                      <span style={{ color: "var(--text-4)" }}>—</span>
                    ) : (
                      <HealthBar value={plugin.health} width={60} />
                    )}
                  </td>

                  {/* Appels 7j + sparkline */}
                  <td>
                    <span style={{ "margin-right": "6px" }}>{plugin.calls7d.toLocaleString()}</span>
                    <Sparkline seed={plugin.calls7d % 100000} width={48} height={14} />
                  </td>

                  {/* Erreurs 7j */}
                  <td style={{ color: plugin.errors7d > 0 ? "var(--danger)" : undefined }}>{plugin.errors7d}</td>

                  {/* Latence p95 */}
                  <td>{plugin.latencyP95ms !== null ? `${plugin.latencyP95ms}ms` : "—"}</td>

                  {/* Dernière erreur */}
                  <td style={{ color: plugin.lastError ? "var(--warn)" : undefined }}>{plugin.lastError ?? "—"}</td>

                  {/* Couches */}
                  <td>
                    <LayerBadges layers={plugin.layers} />
                  </td>

                  {/* Actions */}
                  <td>
                    <button class="btn btn--xs" disabled>
                      Détails
                    </button>
                  </td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>

      {/* ── Section 3 : Flux d'invocations récentes ── */}
      <div class="invoc-stream">
        <div
          style={{
            display: "flex",
            "align-items": "center",
            "justify-content": "space-between",
            "margin-bottom": "8px",
          }}
        >
          <span class="invoc-stream-title">Invocations récentes</span>
          <button class="btn btn--xs" onClick={() => {}}>
            Rafraîchir
          </button>
        </div>
        <div class="invoc-stream-log">
          <For each={MOCK_INVOC_STREAM}>{(line) => <div class="invoc-line">{renderInvocLine(line)}</div>}</For>
        </div>
      </div>
    </div>
  );
}
